import { useInRouterContext } from 'react-router-dom';
import { Wallet } from 'lucide-react';
import { useTranslation } from '../../../application/hooks/useTranslation';
import { formatBDT } from '../../../domain/formatters/currency';
import { formatNumber } from '../../../domain/formatters/date';
import { resolvePrepaid, type InstallmentRows } from '../../../domain/calculators/installments';
import { AmountDisplay } from '../ui/AmountDisplay';
import { Badge } from '../ui/Badge';
import { Card } from '../ui/Card';
import { PayLink } from '../payments/PayLink';
import './installments.css';

export interface AdvanceBalanceCardProps {
  rows?: InstallmentRows;
  advanceBalance?: number;
  /** E07 `prepaidAmount` (ADVANCED rows + advanceBalance). Absent on an older API → falls back to `advanceBalance`. */
  prepaidAmount?: number;
  /** E07 `prepaidMonths` (count of ADVANCED rows). */
  prepaidMonths?: number;
  monthlyAmount?: number;
}

/**
 * Advance card (DEVELOPMENT_PLAN.md §6.3 Q5 & Q7, §3.3 — rule revised by owner decision).
 * Main figure = `prepaidAmount` (Σ ADVANCED rows + the unallocated remainder), sub line "N months prepaid".
 * It falls by one instalment when the monthly job turns an ADVANCED row into PAID. The leftover
 * `advanceBalance` (always < one instalment) is a small secondary "partial credit" line, shown only when > 0.
 * Old API without `prepaidAmount` → the previous behaviour (`advanceBalance` alone). The BD-20
 * "Amount due now" still uses `advanceBalance` (the remainder) and is not affected by this card.
 */
export function AdvanceBalanceCard({ advanceBalance = 0, prepaidAmount, prepaidMonths, monthlyAmount }: AdvanceBalanceCardProps) {
  const { t, locale } = useTranslation();
  const { amount: prepaid, months, leftover: advance } = resolvePrepaid({ advanceBalance, prepaidAmount, prepaidMonths });
  const monthly = typeof monthlyAmount === 'number' && Number.isFinite(monthlyAmount) && monthlyAmount > 0 ? monthlyAmount : null;
  const hasPrepaid = prepaid > 0;
  const hasAdvance = advance > 0;
  const needed = monthly !== null ? Math.max(0, monthly - advance) : null;

  const inRouter = useInRouterContext();

  return (
    <Card data-testid="advance-card">
      <div className="kpi-card-inner">
        <div className="kpi-top">
          <div className="kpi-head-row">
            <span className="kpi-icon-label">
              <span className="kpi-icon-wrap">
                <Wallet size={14} />
              </span>
              {t('installments.advanceCard')}
            </span>
            {hasPrepaid ? (
              <Badge tone="info" data-testid="advance-months">
                {months > 0 ? t('installments.prepaidBadge') : t('installments.partialCreditBadge')}
              </Badge>
            ) : (
              <Badge tone="neutral">{t('installments.zeroCreditBadge')}</Badge>
            )}
          </div>
          <div className="kpi-val">
            {!hasPrepaid ? (
              <span data-testid="advance-none">
                <AmountDisplay amount={0} />
              </span>
            ) : (
              <AmountDisplay amount={prepaid} />
            )}
          </div>
          <div className="kpi-sub">
            {!hasPrepaid ? (
              <span>{t('installments.noAdvance')}</span>
            ) : (
              <>
                {months > 0 && (
                  <span data-testid="advance-prepaid-months">
                    {t(months === 1 ? 'installments.monthPrepaid' : 'installments.monthsPrepaid', {
                      months: formatNumber(months, locale),
                    })}
                  </span>
                )}
                {hasAdvance && (
                  <span
                    data-testid="advance-credit-note"
                    style={{ display: 'block', fontSize: '0.85em', opacity: 0.8 }}
                  >
                    {t('installments.partialCredit', {
                      credit: formatBDT(advance, locale),
                      needed: needed !== null ? formatBDT(needed, locale) : '—',
                    })}
                  </span>
                )}
              </>
            )}
          </div>
        </div>
        <div className="kpi-footer-action">
          {inRouter ? (
            <PayLink to="/payments/bank-slip/upload" className="kpi-link">
              {t('installments.addAdvanceDepositLink')}
            </PayLink>
          ) : (
            <a href="/payments/bank-slip/upload" className="kpi-link">
              {t('installments.addAdvanceDepositLink')}
            </a>
          )}
        </div>
      </div>
    </Card>
  );
}

export default AdvanceBalanceCard;
